import JSZip from 'jszip';
import { paraSlug, slugsUnicos } from '@nyroh/contracts';

import { api } from './api';

/**
 * ============================================================================
 * IMPORTAÇÃO EM MASSA — o que o navegador faz e o que ele NÃO faz
 * ============================================================================
 *
 * O FORMATO DO EXPORT
 *
 * Uma pasta por site dentro do ZIP, com três arquivos:
 *
 *     three-home-643/index.html        ~820 KB — 92% disso é base64
 *     three-home-643/data.json         ~430 KB — as mesmas imagens de novo
 *     three-home-643/system_meta.json  ~280 B  — id, name, industry, subdomain
 *
 * A DIVISÃO DE TRABALHO
 *
 * O navegador faz só o que é barato e só ele pode fazer: abrir o ZIP, ler os
 * três arquivos de cada pasta, subir `index.html` para o R2 e enfileirar um job
 * por site. Tudo que é caro — decodificar base64, subir onze imagens, reescrever
 * o documento, publicar — fica com o worker.
 *
 * A razão é o volume. Cinco mil sites são ~6 GB. Fazer isso na aba significa a
 * aba aberta por horas, e um F5 no meio perdendo tudo. Com um job por site, o
 * pior caso de fechar a aba é parar de ENVIAR: o que já foi enfileirado
 * continua sendo processado, e o que faltou entra na próxima tentativa sem
 * duplicar (a chave de idempotência é o par lote+pasta).
 *
 * OS BYTES NÃO PASSAM PELA API
 *
 * O `index.html` vai por URL pré-assinada direto para o balde PRIVADO. É o
 * mesmo padrão do upload de imagem, e pelo mesmo motivo: 5.000 × 820 KB
 * atravessando o processo Node seriam ~4 GB de egress para mover arquivo de um
 * lado para o outro do mesmo R2.
 */

/** O que uma pasta do ZIP contém — o cadastro lido, o HTML ainda não. */
export interface SiteDoZip {
  pasta: string;
  /**
   * Lê o `index.html` SOB DEMANDA, na hora de subir.
   *
   * A versão anterior guardava a string em `html`. Medido com um ZIP de 600
   * sites: os 600 `index.html` juntos ocupavam **946 MB de heap** — e no
   * navegador as strings são UTF-16, então ~937 MB só de HTML, somados aos
   * ~531 MB que o JSZip mantém do próprio ZIP. Perto de 1,5 GB numa aba, para
   * um dado que é usado UMA vez cada, em série.
   *
   * Com a leitura preguiçosa o pico cai para o ZIP mais um HTML de cada vez:
   * a entrada continua comprimida dentro do JSZip até alguém pedir, e o
   * `enviarLote` descarta assim que o PUT termina.
   */
  lerHtml: () => Promise<string>;
  /** Tamanho do HTML, para a tela mostrar sem carregar o conteúdo. */
  bytes: number;
  /** `system_meta.json` + `data.json` fundidos no que interessa. */
  cliente: {
    nome: string;
    slug: string;
    categoria?: string;
    telefone?: string;
    endereco?: string;
    site?: string;
    nota?: number;
    totalAvaliacoes?: number;
  };
}

export interface ProblemaNoZip {
  pasta: string;
  motivo: string;
}

/**
 * Abre o ZIP e devolve um item por pasta que tenha `index.html`.
 *
 * O `index.html` é o único arquivo INDISPENSÁVEL: sem ele não há site para
 * importar. `system_meta.json` e `data.json` são complementos — quando faltam,
 * o nome sai do nome da pasta e o cadastro fica magro, o que é melhor que
 * recusar a pasta inteira.
 */
export async function lerZip(
  arquivo: File,
): Promise<{ sites: SiteDoZip[]; problemas: ProblemaNoZip[] }> {
  const zip = await JSZip.loadAsync(arquivo);

  // Agrupa por pasta. `index.html` na raiz do ZIP conta como uma pasta sem nome,
  // que é o formato de um export de site único.
  const porPasta = new Map<string, JSZip.JSZipObject[]>();
  zip.forEach((caminho, entrada) => {
    if (entrada.dir) return;
    // __MACOSX/ e ._arquivo aparecem em todo ZIP feito no macOS e não são
    // conteúdo: entrariam como pastas fantasma com um "index.html" ilegível.
    if (caminho.startsWith('__MACOSX/') || caminho.split('/').pop()?.startsWith('._')) return;
    const partes = caminho.split('/');
    const pasta = partes.length > 1 ? partes.slice(0, -1).join('/') : '';
    const lista = porPasta.get(pasta) ?? [];
    lista.push(entrada);
    porPasta.set(pasta, lista);
  });

  const sites: SiteDoZip[] = [];
  const problemas: ProblemaNoZip[] = [];

  for (const [pasta, entradas] of porPasta) {
    const nomeDe = (e: JSZip.JSZipObject) => e.name.split('/').pop() ?? '';
    const indexHtml = entradas.find((e) => nomeDe(e).toLowerCase() === 'index.html');
    if (!indexHtml) {
      // Só reclama de pasta que parecia um site. Uma pasta de imagens soltas
      // não é problema, é ruído.
      if (entradas.some((e) => nomeDe(e).endsWith('.json'))) {
        problemas.push({ pasta: pasta || '(raiz)', motivo: 'não tem index.html' });
      }
      continue;
    }

    const meta = entradas.find((e) => nomeDe(e).toLowerCase() === 'system_meta.json');
    const dados = entradas.find((e) => nomeDe(e).toLowerCase() === 'data.json');

    /*
      O tamanho vem do CABEÇALHO do ZIP, sem descomprimir.
      
      `_data.uncompressedSize` é o que o índice central do arquivo já declara.
      Ler o conteúdo só para saber quantos bytes ele tem seria descomprimir 600
      arquivos de 820 KB para descartar todos — que era exatamente o custo que
      esta mudança existe para eliminar.
    */
    const bytes: number =
      (indexHtml as unknown as { _data?: { uncompressedSize?: number } })._data
        ?.uncompressedSize ?? 0;

    // Um `index.html` de zero byte é pasta quebrada, e isso dá para saber sem
    // abrir. Vazio "com espaços" só aparece na hora de subir, e ali o erro é
    // reportado como falha daquele site.
    if (bytes === 0) {
      problemas.push({ pasta: pasta || '(raiz)', motivo: 'index.html está vazio' });
      continue;
    }

    const m = meta ? await lerJson(meta) : null;
    const d = dados ? await lerJson(dados) : null;
    const nomeDaPasta = (pasta.split('/').pop() || arquivo.name.replace(/\.zip$/i, '')).trim();

    sites.push({
      pasta: pasta || nomeDaPasta,
      lerHtml: () => indexHtml.async('string'),
      bytes,
      cliente: {
        nome: texto(m?.name) ?? texto(d?.name) ?? nomeDaPasta,
        // Provisório: `slugsUnicos` reescreve todos no fim, com o lote inteiro
        // à vista. Aqui não dá para saber quem vai colidir com quem.
        slug: paraSlug(nomeDaPasta),
        /*
          A categoria vem de `categories[1]`, não de `[0]`.

          No export do Google Places o primeiro item é sempre um rótulo
          estrutural — "establishment", "point_of_interest" —, e o segundo é o
          ramo de verdade ("furniture_store"). `system_meta.industry` traz o
          mesmo "establishment" inútil.
        */
        ...opcional('categoria', texto(arr(d?.categories)?.[1]) ?? texto(arr(d?.categories)?.[0])),
        ...opcional('telefone', texto(d?.phoneNumber)),
        ...opcional('endereco', texto(d?.address)),
        ...opcional('site', texto(d?.website)),
        ...(typeof d?.rating === 'number' ? { nota: d.rating } : {}),
        ...(typeof d?.reviewCount === 'number' ? { totalAvaliacoes: d.reviewCount } : {}),
      },
    });
  }

  return { sites, problemas };
}

async function lerJson(e: JSZip.JSZipObject): Promise<Record<string, unknown> | null> {
  try {
    return JSON.parse(await e.async('string')) as Record<string, unknown>;
  } catch (err) {
    // JSON quebrado não invalida a pasta: o `index.html` é o que importa, e o
    // cadastro cai para o nome da pasta.
    console.warn(`[importacao] "${e.name}" não é JSON válido:`, err);
    return null;
  }
}

const texto = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;
const arr = (v: unknown): unknown[] | undefined => (Array.isArray(v) ? v : undefined);
const opcional = <K extends string>(k: K, v: string | undefined) =>
  (v ? { [k]: v } : {}) as Record<K, string> | Record<string, never>;

export interface AndamentoEnvio {
  enviados: number;
  total: number;
  /** A pasta que está subindo agora, para a tela ter o que mostrar. */
  atual: string;
}

export interface ResultadoEnvio {
  enfileirados: number;
  jaExistiam: number;
  falhas: Array<{ pasta: string; motivo: string }>;
}

/**
 * Sobe os HTMLs e enfileira um job por site.
 *
 * Em série, e não em paralelo: cada PUT é de ~820 KB, e vinte simultâneos
 * saturam a banda de subida — o efeito seria vinte timeouts no lugar de vinte
 * arquivos. O ganho de paralelismo aqui seria pequeno e o risco, grande.
 *
 * Enfileira de 200 em 200 pela rota de lote, e não um POST por site: cinco mil
 * POSTs sequenciais são minutos de tela travada só em ida e volta de rede.
 */
export async function enviarLote(
  lote: string,
  sites: SiteDoZip[],
  aoProgredir?: (a: AndamentoEnvio) => void,
): Promise<ResultadoEnvio> {
  /*
    Slugs únicos no LOTE INTEIRO, antes de qualquer envio.

    Duas pastas com nomes parecidos normalizam para o mesmo slug, e slug é
    UNIQUE em `clientes`. Sem desempatar aqui, a segunda cairia no
    ON CONFLICT DO NOTHING do import e o job publicaria por cima do site da
    primeira — dois negócios diferentes no mesmo endereço.
  */
  const slugs = slugsUnicos(sites.map((s) => s.cliente.nome || s.pasta));

  const falhas: ResultadoEnvio['falhas'] = [];
  const prontos: Array<{ site: SiteDoZip; slug: string; chaveHtml: string }> = [];

  for (const [i, site] of sites.entries()) {
    aoProgredir?.({ enviados: i, total: sites.length, atual: site.pasta });
    try {
      /*
        Lê AQUI, sobe, e deixa a string ser coletada.
        
        É o ponto da leitura preguiçosa: `html` existe só dentro desta volta do
        laço. Guardá-la em `prontos` traria de volta os 946 MB que a medição
        com 600 sites mostrou.
      */
      const html = await site.lerHtml();
      if (!html.trim()) throw new Error('o index.html está vazio');

      const bytes = new Blob([html]).size;
      const assinatura = await api.chamar('importacoes.assinarEnvio', {
        corpo: { lote, pasta: site.pasta, arquivo: 'index.html', bytes },
      });
      const r = await fetch(assinatura.url, {
        method: 'PUT',
        headers: { 'content-type': 'text/html' },
        body: html,
      });
      if (!r.ok) throw new Error(`o R2 respondeu ${r.status}`);
      prontos.push({ site, slug: slugs[i]!, chaveHtml: assinatura.chaveR2 });
    } catch (e) {
      console.error(`[importacao] falha ao subir "${site.pasta}":`, e);
      falhas.push({ pasta: site.pasta, motivo: explicarFalhaDeEnvio(e) });
    }
  }
  aoProgredir?.({ enviados: sites.length, total: sites.length, atual: '' });

  let enfileirados = 0;
  let jaExistiam = 0;

  for (let i = 0; i < prontos.length; i += 200) {
    const fatia = prontos.slice(i, i + 200);
    const r = await api.chamar('fila.enfileirarLote', {
      corpo: {
        jobs: fatia.map(({ site, slug, chaveHtml }) => ({
          tipo: 'importar_site_externo' as const,
          payload: {
            lote,
            pasta: site.pasta,
            chaveHtml,
            cliente: { ...site.cliente, slug },
          },
          // Lote + pasta é a chave natural do trabalho: reenviar o mesmo ZIP
          // não gera um segundo job para o mesmo site.
          chaveIdempotencia: `importar_site_externo:${lote}:${site.pasta}`,
          maxTentativas: 3,
        })),
      },
    });
    enfileirados += r.enfileirados;
    jaExistiam += r.jaExistiam;
  }

  return { enfileirados, jaExistiam, falhas };
}

/**
 * Traduz a falha do PUT pré-assinado em algo acionável.
 *
 * O NAVEGADOR NÃO CONTA QUAL FOI O PROBLEMA
 *
 * Quando o preflight de CORS é barrado, o `fetch` rejeita com um
 * `TypeError: Failed to fetch` — a MESMA exceção de estar sem rede. O detalhe
 * ("No 'Access-Control-Allow-Origin' header") só existe no console do DevTools,
 * por decisão da especificação: o JavaScript não pode distinguir os dois casos,
 * senão viraria um scanner de portas.
 *
 * Então a tela dizia "Failed to fetch" e ficava por isso mesmo. Como o destino
 * aqui é sempre um bucket do R2 numa origem diferente, a causa mais provável é
 * a política de CORS do bucket — e vale mais dizer isso, com o conserto junto,
 * do que repetir a mensagem do navegador.
 */
function explicarFalhaDeEnvio(e: unknown): string {
  if (e instanceof TypeError) {
    return (
      'o navegador bloqueou o envio para o bucket (CORS ou conexão). ' +
      'Se for CORS: no painel da Cloudflare, R2 → bucket privado → Settings → ' +
      'CORS Policy, permita PUT a partir da origem deste painel. Ver docs/DEPLOY.md §2.1.'
    );
  }
  return e instanceof Error ? e.message : 'falha ao enviar';
}

/** O andamento da fila, para a tela mostrar sem inventar número. */
export async function progressoDaFila(): Promise<{
  pendente: number;
  processando: number;
  concluido: number;
  falhou: number;
  deadLetter: number;
}> {
  const r = await api.chamar('fila.resumo', {});
  const n = (status: string) => r.porTipoEStatus[`importar_site_externo:${status}`] ?? 0;
  return {
    pendente: n('pendente'),
    processando: n('processando'),
    concluido: n('concluido'),
    falhou: n('falhou'),
    deadLetter: r.deadLetter,
  };
}
